// Realtime wiring (spec §8 "realtime updates apply" — flagged by T26's e2e
// sweep as unbuilt: `grep -rn "\.channel(" portal/src` found nothing).
// Subscribes one Supabase Realtime channel per selected business to
// `bookings` and `booking_events` (RLS already scopes rows to a live member
// of that business — see the publication migration's header) and
// invalidates every TanStack Query key those two tables feed, so the
// bookings list, the dashboard and the check-in desk's offline manifest
// pick up a change from any device/tab without a manual refresh.
//
// The publication membership (`ALTER PUBLICATION supabase_realtime ADD
// TABLE`) is a separate, one-time migration
// (20260926000046_realtime_bookings_publication.sql) — without it no event
// reaches ANY subscriber regardless of RLS, so this module alone is not
// enough to prove the wiring end-to-end; see the E2E spec for that.
import { useEffect, useId, useRef } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import type { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import { useAuth } from '../auth/authContext';
import { cancelThenInvalidate } from '@/lib/query/refresh';

/** Every query key that reads `bookings` or `booking_events` for a
 * business. `booking-events` is keyed by bookingId, not businessId — there
 * is no cheap prefix match for "this business's booking-events", so it is
 * invalidated unscoped; only the currently-mounted booking's events query
 * (if any) actually refetches, and that booking is necessarily already
 * scoped to the selected business (its own query key carries businessId). */
export function invalidateBookingsQueries(queryClient: QueryClient, businessId: string): void {
  cancelThenInvalidate(queryClient, { queryKey: ['bookings', businessId] });
  cancelThenInvalidate(queryClient, { queryKey: ['bookings-range', businessId] });
  cancelThenInvalidate(queryClient, { queryKey: ['bookings-outstanding', businessId] });
  cancelThenInvalidate(queryClient, { queryKey: ['booking', businessId] });
  cancelThenInvalidate(queryClient, { queryKey: ['booking-events'] });
  cancelThenInvalidate(queryClient, { queryKey: ['desk-manifest', businessId] });
}

/** One channel, both tables, the same handler — the caller decides what
 * "changed" means (here: invalidate everything above). Exported standalone
 * (not just inlined in the hook) so it's unit-testable against a stubbed
 * Supabase client without rendering anything.
 *
 * `topic` is the caller's responsibility (fix round 1, Important #1): the
 * installed realtime-js dedupes `supabase.channel()` calls by topic string
 * — `RealtimeClient.channel()` returns the EXISTING channel object if one
 * with that topic is still in its list, which it is for a beat after a
 * route change, because a leaving channel isn't removed until the server's
 * leave-ack arrives. Two screens sharing one topic (`bookings-realtime:
 * <businessId>`, this module's own earlier bug) meant an in-app navigation
 * between them handed the new screen a channel already mid-leave: its
 * `.on()` bindings were silently dropped as duplicates, or — if the new
 * screen mounted before the old one's cleanup ran — `.on()` threw ("cannot
 * add postgres_changes callbacks after subscribe()"). Every mount now gets
 * its own topic (see `useBookingsRealtime`'s `useId()`), so two screens
 * never contend for the same channel object regardless of mount/unmount
 * order.
 *
 * `onStatus`, when given, is the raw `.subscribe()` status callback —
 * reported states other than `SUBSCRIBED`/`CLOSED` (`CHANNEL_ERROR`,
 * `TIMED_OUT`) otherwise fail silently: no error, no fallback polling,
 * just a screen that quietly stops updating. */
export function subscribeToBookingsRealtime(
  supabase: SupabaseClient,
  businessId: string,
  onChange: () => void,
  topic: string = `bookings-realtime:${businessId}`,
  onStatus?: (status: string, err?: Error) => void,
): RealtimeChannel {
  const channel = supabase.channel(topic);
  channel
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'bookings', filter: `business_id=eq.${businessId}` },
      onChange,
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'booking_events', filter: `business_id=eq.${businessId}` },
      onChange,
    )
    .subscribe((status, err) => onStatus?.(status, err));
  return channel;
}

const INVALIDATE_DEBOUNCE_MS = 250;

/** Mount this once per screen that reads live booking data (the bookings
 * list, the dashboard, check-in). Re-subscribes on a business switch
 * (businessId changes) and unsubscribes on unmount — which covers sign-out
 * too, since every business route sits under the guarded root layout that
 * stops rendering them the moment the session goes away.
 *
 * Each call site gets its own channel topic (`useId()`, stable for the
 * component instance's lifetime) — see `subscribeToBookingsRealtime`'s doc
 * for why sharing one topic across the list/dashboard/check-in screens
 * left the newly-mounted screen with a dead subscription after an in-app
 * navigation between them (fix round 1, Important #1).
 *
 * A single Postgres write can touch both `bookings` and `booking_events`
 * (e.g. a desk balance record), firing this twice in close succession;
 * invalidation is debounced 250ms so a busy property refetches once, not
 * once per row event (fix round 1, Minor #7). */
export function useBookingsRealtime(businessId: string | null): void {
  const { supabase } = useAuth();
  const queryClient = useQueryClient();
  const instanceId = useId();
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!businessId) return;
    const topic = `bookings-realtime:${businessId}:${instanceId}`;

    const invalidate = () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        debounceTimer.current = null;
        invalidateBookingsQueries(queryClient, businessId);
      }, INVALIDATE_DEBOUNCE_MS);
    };

    const channel = subscribeToBookingsRealtime(supabase, businessId, invalidate, topic, (status, err) => {
      if (status !== 'SUBSCRIBED' && status !== 'CLOSED') {
        // No Sentry client is wired into the portal frontend yet (only the
        // edge functions have one, `supabase/functions/_shared/sentry.ts`)
        // — console.error is the honest floor until one is, and a natural
        // place to add a breadcrumb/report call later (fix round 1, Minor #6).
        console.error(`bookingsRealtime: subscription ${status} for ${topic}`, err);
      }
    });

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      supabase.removeChannel(channel);
    };
  }, [supabase, businessId, queryClient, instanceId]);
}
